import { Fragment } from "react"
import { Button, IconButton } from '../../components/button';
import { Chip } from "../../components/chip"
import { BaseContainer } from "../../components/container"
import { BackButton } from "../../components/buttons/back_btn"
import { useHistory } from "react-router-dom"
import { useContext, useEffect, useState } from "react";
import { validateEmail } from '../../components/text_input'

import ActionDialog from "../../components/dialogs/action_dialog";
import { InfoPanel } from "../../components/info_panel"
import ListItem from "../../components/lists/list_item"
import { HeaderTitle, PageDescription } from "../../components/page_sections"
import { StickyFooterGroup } from "../../components/sticky_footer_group"
import { formatDate } from "../../utils/common"
import { Divider } from "../../components/divider"
import { VaccineEventKey, VaccineRecord, vaccineEventName } from "../records/utils"
import { TextField, FormHelperText } from "@material-ui/core"
import { axiosInstance } from "../../utils/axiosApi";
import Collapse from '@material-ui/core/Collapse';
import Alert from '../../components/alert';
import CloseIcon from '@material-ui/icons/Close';

type GillickPageProps = {
    location : {
        state : {
            user : {
                preferred_name: string,
                full_name: string,
                uuid: string
            },
            clinic : {
                full_name: string
            }
        }
    }
}

export const GillickReview = (props:GillickPageProps) => {
    const history = useHistory()

    const fullName = props.location.state?.user.full_name;
    const clinicName = props.location.state?.clinic.full_name;

    return (
        <BaseContainer noHeader>

            <BackButton onClick={() => {
                history.push('/home')
            }} />

            <HeaderTitle>
                Consent Update Required
            </HeaderTitle>

            <p>In line with GDPR regulations, children older than 13 years of age must provide (refreshed) consent for their health records to be managed by an adult. This must be confirmed by a certified clinician.</p>
            <p>Please contact {clinicName} to make arrangements.</p>
            <p>If consent is not updated on the Imunis platform by the child’s 13th birthday, records will cease to be accessible via this account, until re-instated.</p>
            <p>A PDF records copy may be downloaded via this account until that date.</p>

        </BaseContainer>
    )
}

export const GillickSetEmail = (props:GillickPageProps) => {
    const history = useHistory()

    const preferredName = props.location.state?.user.preferred_name;
    const fullName = props.location.state?.user.full_name;
    const uuid = props.location.state?.user.uuid;

    const [error, setError] = useState('')
    const [showError, setShowError] = useState(false)
    const [showSuccessDialog, setShowSuccessDialog] = useState(false)
    const [loading, setLoading] = useState(false)

    const [errorMsgs, setErrorMsgs] = useState({ email: ''})
    const [state, setState] = useState({
        email: '',
    })

    const { email } = state

    const validateInput = (text:string, type:string) => {
        let error = ''
        switch (type) {
            case 'email':
                error = validateEmail(text) || ''
                break
            default:
                break;
        }
        setErrorMsgs(prevState => ({...prevState, [type]: error}))
        setState(prevState => ({...prevState, [type]: text}))
    }

    function handleFormChange(
        event: React.ChangeEvent<HTMLInputElement>,
        type: 'email' | 'password' | 'username'
    ) {
        const text = event.target.value
        validateInput(text, type)
    }

    async function handleSetEmail() {
        setLoading(true)
        try {
            await axiosInstance.post(`/user/${uuid}/profile/transition_email/`, { transition_email: state.email });
            setShowSuccessDialog(true);
        } catch (error) {
            // Show a transition email already in use against the email field; any other
            // failure (including OpenAPI schema validation) needs the error dialog.
            if (error?.response?.data?.error_code == 'email_in_use') {
                setErrorMsgs(prevState => ({
                    ...prevState,
                    email: error.response.data.error
                }))
            } else {
                console.error(error)
                setError('Server Error')
                setShowError(true)
            }
        } finally {
            setLoading(false)

        }
    }

    const validated = !(
        errorMsgs.email
    )
    const enabled = (
        email && validated
    )

    return (
        <BaseContainer noHeader showCta extraComponent={
            <StickyFooterGroup>
                <Button primary fullWidth onClick={handleSetEmail} disabled={!enabled}>
                    Set Email
                </Button>
            </StickyFooterGroup>
        }>

            <BackButton onClick={() => {
                history.push('/home')
            }} />

            <HeaderTitle>
                Young Person Account Transition
            </HeaderTitle>

            <p>From age 16, young people considered able are required to have their own account.</p>
            <p>To allow this transition to happen without loss of records, please:</p>
            <ol>
               <li>Ask whether the person would like to create their own Imunis account in order to manage their vaccination health data</li>
               <li>If they do, provide an email address below, to which an onboarding email will be sent when they turn 16.</li>
            </ol>

            <p>If an email address is not provided before they turn 16, they will not be issued with an account invitation and their
                information will no longer be accessible to you on this account. If they change their mind, the email address may be
                deleted (follow Records &gt; Profile) or they can simply ignore the invitation.</p>

            <p>A PDF copy of their records will remain available on your account prior to their 16th birthday.</p>

            <Collapse in={showError}>
                <Alert
                    severity='error'
                    action={
                        <IconButton
                            aria-label="close"
                            color="inherit"
                            size="small"
                            onClick={() => {
                                setShowError(false)
                            }}
                        >
                            <CloseIcon fontSize="inherit" />
                        </IconButton>
                    }
                >
                    {error}
                </Alert>
            </Collapse>

            <TextField
                id="transitionName"
                name="transitionName"
                label="Transition Email Address"
                placeholder="Transition Email Address"
                fullWidth
                margin="normal"
                onChange={(event) => handleFormChange(
                    event as React.ChangeEvent<HTMLInputElement>,
                    'email'
                )}
                error={!!errorMsgs.email}
                helperText={errorMsgs.email || `Enter email address to send invite to.`}
                required
            />

            <ActionDialog
                title='Email Set'
                content={<>
                    The transition email for {fullName} has been set. An invitation to onboard as an account holder will be sent to this email address on {fullName}'s 16th birthday.
                    <br/><br/>
                    To change this go to Records &gt; Profile
                </>}
                action_label='OK'
                action_callback={() => history.push('/home')}
                openDialog={showSuccessDialog}
                setOpenDialog={setShowSuccessDialog}
            />

        </BaseContainer>
    )
}
